import React, { useState } from "react";
import {
  Box,
  Text,
  Button,
  VStack,
  HStack,
  Spinner,
  Input,
  InputGroup,
  InputRightElement,
} from "@chakra-ui/react";
import { ChevronDownIcon, SearchIcon } from "@chakra-ui/icons";
import {
  useLicensePlateNumberValidator,
  useCarMakers,
  useModelLines,
  useModifications,
  useYears,
} from "../hooks/useVehicleData";
import useNotification from "../../../utils/useNotification";

export interface CarItem {
  id?: number;
  name?: string;
  value: string;
  label: string;
  isPopular?: boolean;
}

interface VehicleSearchFormProps {
  onCancel: () => void;
  onConfirm: (values: any) => void;
  selectedValues?: Record<string, any>;
  onValueChange: (field: string, value: any) => void;
  onDropdownOpen: (field: string, data: CarItem[], title: string) => void;
  onError?: (message: string) => void;
  errorMessage?: string;
  showCancelButton?: boolean;
  confirmButtonText?: string;
  isConfirmButtonDisabled?: boolean;
  onLpnSearch?: (licensePlateNumber: string) => void;
}

const VehicleSearchForm: React.FC<VehicleSearchFormProps> = ({
  onCancel,
  onConfirm,
  selectedValues = {},
  onValueChange,
  onDropdownOpen,
  onError,
  errorMessage,
  showCancelButton = true,
  confirmButtonText = "Confirm",
  isConfirmButtonDisabled = false,
  onLpnSearch,
}) => {
  const isLicensePlateNumberValid = useLicensePlateNumberValidator();
  const [licensePlateNumber, setLicensePlateNumber] = useState(
    selectedValues.licensePlateNumber || ""
  );
  const [lpnError, setLpnError] = useState("");
  const { carMakers, loading: loadingMakers } = useCarMakers(onError);
  const { modelLines, loading: loadingModels } = useModelLines(
    selectedValues.carMaker,
    onError
  );
  const { years, loading: loadingYears } = useYears(
    selectedValues.modelLine,
    selectedValues.carMaker,
    onError
  );
  const { modifications, loading: loadingMods } = useModifications(
    selectedValues.modelLine,
    selectedValues.year,
    selectedValues.carMaker,
    onError
  );

  const getDropdownData = (field: string): CarItem[] => {
    switch (field) {
      case "carMaker":
        return carMakers;
      case "modelLine":
        return modelLines;
      case "year":
        return years;
      case "modification":
        return modifications;
      default:
        return [];
    }
  };

  const getFieldLabel = (field: string): string => {
    switch (field) {
      case "carMaker":
        return "Car Maker";
      case "modelLine":
        return "Model Line";
      case "year":
        return "Year";
      case "modification":
        return "Modification";
      default:
        return "";
    }
  };

  const getSelectedItems = () => {
    const selectedItems: Record<string, any> = {};
    if (selectedValues.carMaker) {
      const selectedItem = getDropdownData("carMaker").find(
        (item) => item.value === selectedValues.carMaker
      );
      selectedItems.carMaker = selectedItem;
    }
    if (selectedValues.modelLine) {
      const selectedItem = getDropdownData("modelLine").find(
        (item) => item.value === selectedValues.modelLine
      );
      selectedItems.modelLine = selectedItem;
    }
    if (selectedValues.year) {
      selectedItems.year = selectedValues.year;
    }
    if (selectedValues.modification) {
      const selectedItem = getDropdownData("modification").find(
        (item) => item.value === selectedValues.modification
      );
      selectedItems.modification = selectedItem;
    }

    return selectedItems;
  };

  const handleFieldPress = (field: string) => {
    onDropdownOpen(field, getDropdownData(field), getFieldLabel(field));
  };

  const isFieldEnabled = (field: string): boolean => {
    switch (field) {
      case "carMaker":
        return true;
      case "modelLine":
        return !!selectedValues.carMaker;
      case "year":
        return !!selectedValues.carMaker && !!selectedValues.modelLine;
      case "modification":
        return (
          !!selectedValues.carMaker &&
          !!selectedValues.modelLine &&
          !!selectedValues.year
        );
      default:
        return false;
    }
  };

  const RenderField: React.FC<{ field: string; data: CarItem[] }> = ({
    field,
    data,
  }) => {
    const label = getFieldLabel(field);
    const value = selectedValues[field];
    const selectedItem = data.find((item) => item.value === value);
    const displayText = selectedItem ? selectedItem.label : `${label}`;
    const isLoading =
      (field === "carMaker" && loadingMakers) ||
      (field === "modelLine" && selectedValues.carMaker && loadingModels) ||
      (field === "year" && selectedValues.modelLine && loadingYears) ||
      (field === "modification" && selectedValues.year && loadingMods);
    const isEnabled = isFieldEnabled(field) && !isLoading;

    const isPreFilledByVehicleData =
      (field === "carMaker" && selectedValues.isCarMakerPreFilled) ||
      (field === "modelLine" && selectedValues.isModelLinePreFilled) ||
      (field === "year" && selectedValues.isYearPreFilled) ||
      (field === "modification" && selectedValues.isModificationPreFilled);

    return (
      <Box key={field} mb={2}>
        <Button
          variant="outline"
          w="full"
          h="50px"
          justifyContent="space-between"
          onClick={() =>
            isEnabled && !isPreFilledByVehicleData && handleFieldPress(field)
          }
          isDisabled={!isEnabled}
          bg={!isEnabled ? "gray.100" : "white"}
          color={!isEnabled ? "gray.400" : "gray.700"}
          _hover={isEnabled ? { bg: "gray.50" } : {}}
          cursor={isPreFilledByVehicleData ? "not-allowed" : "pointer"}
        >
          <Text
            color={
              !value
                ? "gray.500"
                : isEnabled || isPreFilledByVehicleData
                ? "gray.700"
                : "gray.400"
            }
            flex={1}
            fontWeight={"normal"}
            textAlign="left"
            fontSize={"14px"}
          >
            {displayText?.replace(" ⭐", "")}
          </Text>
          {isLoading && !selectedItem ? (
            <Spinner size="sm" color="blue.500" />
          ) : (
            <ChevronDownIcon
              color={
                isEnabled && !isPreFilledByVehicleData ? "gray.500" : "gray.300"
              }
              boxSize={5}
            />
          )}
        </Button>
      </Box>
    );
  };

  const handleLpnSearch = () => {
    if (licensePlateNumber && onLpnSearch) {
      const cleanedLicensePlateNumber = licensePlateNumber
        .trim()
        .toUpperCase()
        .replace(/[\s-]+/g, "");
      if (isLicensePlateNumberValid(cleanedLicensePlateNumber)) {
        setLpnError("");
        onValueChange("licensePlateNumber", cleanedLicensePlateNumber);
        onValueChange("isLicensePlateNumberPreFilled", true);
        onLpnSearch(cleanedLicensePlateNumber);
      } else {
        setLpnError("Invalid License Plate Number");
      }
    }
  };

  const LPNInputField = () => {
    return (
      <Box mb={2}>
        <Text fontSize="sm" fontWeight="medium" mb={2} color="gray.700">
          License Plate Number
        </Text>
        <InputGroup>
          <Input
            placeholder="Enter License Plate Number"
            value={licensePlateNumber}
            onChange={(e) =>
              setLicensePlateNumber(e.target.value?.toUpperCase() || "")
            }
            bg="white"
            border="1px solid"
            height={"50px"}
            borderColor="gray.300"
            _hover={{ borderColor: "gray.400" }}
            _focus={{
              borderColor: "blue.500",
              boxShadow: "0 0 0 1px #3182ce",
            }}
            disabled={selectedValues.isLicensePlateNumberPreFilled}
          />
          <InputRightElement>
            <Button
              size="sm"
              mr={1}
              mt={"10px"}
              onClick={handleLpnSearch}
              isDisabled={
                !licensePlateNumber ||
                selectedValues.isLicensePlateNumberPreFilled
              }
              cursor={
                !licensePlateNumber ||
                selectedValues.isLicensePlateNumberPreFilled
                  ? "not-allowed"
                  : "pointer"
              }
              colorScheme="blue"
              style={{
                alignItems: "center",
                display: "flex",
              }}
              disabled={selectedValues.isLicensePlateNumberPreFilled}
              _hover={{
                transform: "scale(1.01)",
                transition: "transform 0.2s ease-in-out",
              }}
              _active={{
                transform: "scale(0.99)",
                transition: "transform 0.2s ease-in-out",
              }}
            >
              <SearchIcon color={"#fff"} />
            </Button>
          </InputRightElement>
        </InputGroup>
        {lpnError && (
          <Text color="red.500" fontSize="sm" mt={1} mb={1}>
            {lpnError}
          </Text>
        )}
      </Box>
    );
  };

  return (
    <Box>
      <VStack spacing={4} align="stretch" mb={4}>
        <LPNInputField />
        <RenderField field="carMaker" data={carMakers} />
        <RenderField field="modelLine" data={modelLines} />
        <RenderField field="year" data={years} />
        <RenderField field="modification" data={modifications} />
      </VStack>

      {errorMessage && (
        <Text color="red.500" fontSize="sm" mt={1} mb={1}>
          {errorMessage}
        </Text>
      )}

      <HStack spacing={3} mt={1} mb={4}>
        {showCancelButton && (
          <Button
            variant="outline"
            flex={1}
            onClick={onCancel}
            color="gray.600"
          >
            Cancel
          </Button>
        )}
        <Button
          colorScheme="blue"
          flex={1}
          onClick={() =>
            !isConfirmButtonDisabled && onConfirm(getSelectedItems())
          }
          isDisabled={isConfirmButtonDisabled}
          color={"white"}
          // bgGradient={bgGradient}
          _hover={{
            transform: "scale(1.01)",
            transition: "transform 0.2s ease-in-out",
          }}
          _active={{
            transform: "scale(0.99)",
            transition: "transform 0.2s ease-in-out",
          }}
        >
          {confirmButtonText}
        </Button>
      </HStack>
    </Box>
  );
};

export default VehicleSearchForm;
